/**
 * LegacyPaymentGateway
 *
 * Handle listing activation for free listings
 *
 *
 */
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useRentalManagerUserPropertyActivateWebApiHandler_activate } from '@zg-rentals/rental-manager-env';

import { Paragraph } from '@zillow/constellation-10';

import { trackGtmEvent } from '../../../../../client/analytics';
import { ZRM_GTM_EVENTS } from '../../../../../client/analytics/gtmEvents';

import { useSelector } from '../../../../selectors/useSelector';

import PropertiesActions from '../../../../actions/PropertiesActions';

import ChargeAndActivateListing from '../../../../model/ChargeAndActivateListing';
import PayAndPublishCSEvents from '../../../../model/ClickstreamEvent/PayAndPublishCSEvents';

import PaymentErrorModal from '../../../../components/Modal/PaymentErrorModal';

import { ACTIVATE_LISTING_MUTATION_KEY } from '../../../../constants/QueryConstants';

import type { DatarouterError } from '@zg-rentals/rental-manager-env';
import type { ChargeAndActivateListingProps } from '../../../../model/ChargeAndActivateListing';

type Props = {
  displayPaymentGateway: boolean;
  updateDisplayPaymentGateway: (arg: boolean) => void;
  onChargeAndActivateListingResponse: (arg: { success: boolean; response: ChargeAndActivateListingProps }) => void;
};

const LegacyPaymentGateway = ({
  displayPaymentGateway = false,
  updateDisplayPaymentGateway,
  onChargeAndActivateListingResponse,
}: Props) => {
  const dispatch = useDispatch();

  const queryClient = useQueryClient();

  const {
    properties: {
      currentProperty,
      currentProperty: { listingAlias, active },
    },
    user,
  } = useSelector((state) => state);

  const [hasError, setHasError] = useState<{
    display: boolean;
    errorMessage?: string | JSX.Element;
    isInMaintenanceMode?: boolean;
    onClose?: () => void;
  }>({
    display: false,
  });

  const { mutate: activate } = useRentalManagerUserPropertyActivateWebApiHandler_activate({
    mutationKey: ACTIVATE_LISTING_MUTATION_KEY,
  });

  useEffect(() => {
    if (displayPaymentGateway && !active && listingAlias) {
      activateListing();
    }
  }, [displayPaymentGateway, active, listingAlias]);

  const activateListing = () => {
    return activate(
      { getParams: { listingAlias } },
      {
        onSuccess: (response) => {
          trackGtmEvent({ event: ZRM_GTM_EVENTS.RENTAL_MANAGER_PUBLISH });

          PayAndPublishCSEvents.ActivateListing({ currentProperty, user });

          updateDisplayPaymentGateway(false);
          onChargeAndActivateListingResponse({
            success: true,
            response: ChargeAndActivateListing({
              activation: {
                dateActivatedTimestamp: response?.activatedMs,
                dateUpdatedTimestamp: response?.updatedMs,
                expirationDateTimestamp: response?.expirationMs,
                ...response,
              },
            }),
          });

          dispatch(
            PropertiesActions.updatePostActivationState({
              daysExtended: response?.daysExtended,
              displayPostActivationModal: true,
            }),
          );

          queryClient.invalidateQueries({ queryKey: ['getProperties'], refetchType: 'all' });
        },
        onError: (error) => {
          handleActivationError(error as DatarouterError);
        },
      },
    );
  };

  const handleActivationError = (error: DatarouterError) => {
    const { errorDetails, message: errorMessage } = error;

    const { listingActivationFailed, activationError, isInMaintenanceMode, floorPlans } = (errorDetails || {}) as {
      listingActivationFailed?: boolean;
      activationError?: { message?: string };
      isInMaintenanceMode?: boolean;
      floorPlans?: Array<Record<string, unknown>>;
    };

    // general message
    let message = errorMessage;

    if (listingActivationFailed) {
      if (activationError?.message) {
        // more specific message
        message = activationError?.message;
      }
    }

    if (floorPlans) {
      const errorsObject: Record<string, unknown> = {};

      Object.values(floorPlans).map((floorPlan) => {
        return Object.entries(floorPlan).map(([key, value]) => {
          if (!errorsObject[key]) {
            errorsObject[key] = value;
          }
        });
      });

      setHasError({
        display: true,
        errorMessage: (
          <>
            <Paragraph>{message}</Paragraph>
            {Object.values(errorsObject).map((error) => (
              <Paragraph key={error as string}>{error as string}</Paragraph>
            ))}
          </>
        ),
        isInMaintenanceMode,
        onClose: () => {
          setHasError({ display: false });
        },
      });
    }

    setHasError({
      display: true,
      errorMessage: message,
      isInMaintenanceMode,
      onClose: () => {
        setHasError({ display: false });
      },
    });
  };

  return (
    <div data-testid="LegacyPaymentGateway">
      {hasError.display ? (
        <PaymentErrorModal
          // @ts-expect-error
          errorMessage={hasError.errorMessage}
          isInMaintenanceMode={hasError.isInMaintenanceMode}
          // @ts-expect-error
          onClose={hasError.onClose}
        />
      ) : null}
    </div>
  );
};

export default LegacyPaymentGateway;
